<script setup>
// BonBoZ: thanh công cụ hiện khi rê chuột vào tin — trả lời, chuyển tiếp, thả
// cảm xúc, và menu ⋯. Trước đây mọi thứ nằm sau chuột phải, mà người trực quen
// Pancake thì không ai nghĩ tới việc bấm chuột phải.
//
// Thanh `absolute` nên không chiếm chỗ trong row. Chỗ cho nó là margin của ô
// bubble bên `Message.vue` (lane 12rem), chỉ chừa khi khung chat ≥ 32rem.
import { ref, computed, nextTick } from 'vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  // Bộ emoji Zalo/Pancake đang dùng. Zalo chỉ nhận đúng danh sách mã của họ nên
  // không thả emoji tự do được — xem reactionIcons.ts phía cầu nối.
  emojiChoPhep: {
    type: Array,
    default: () => ['👍', '❤️', '😆', '😮', '😢', '😠'],
  },
  choPhepTraLoi: { type: Boolean, default: false },
  choPhepCamXuc: { type: Boolean, default: false },
  // Tin của chính mình đã thả cảm xúc chưa — để đổi nút thành "gỡ".
  daThaCamXuc: { type: Boolean, default: false },
  // 'left' = thanh nằm bên trái bong bóng (tin của mình).
  phia: { type: String, default: 'right' },
  // Tên người gửi + giờ. Khung rộng: xếp dọc trên viên thuốc trong lane
  // 10.5rem. Khung hẹp: cả cụm xuống dưới bubble, xếp ngang.
  // Hai thế do container query cuối `Message.vue` quyết định.
  chuThich: { type: String, default: '' },
});

const emit = defineEmits([
  'traLoi',
  'chuyenTiep',
  'camXuc',
  'goCamXuc',
  'moMenu',
]);

const { t } = useI18n();

const hienBangChon = ref(false);
const nutMenu = ref(null);
const goc = ref(null);

const dongBangChon = () => {
  hienBangChon.value = false;
};

// Ba đường đóng bảng chọn. Trước đây chỉ có MỘT: bấm lại đúng nút mặt cười.
// Rê chuột ra chỗ khác thì thanh công cụ mờ đi (`opacity-0`) nên trông như đã
// đóng, nhưng `hienBangChon` vẫn bật — quay lại là bảng chọn hiện nguyên đó,
// không ai hiểu vì sao.
//
// Rê chuột ra ngoài: bảng chọn nằm TRONG thanh công cụ nên di chuột từ nút sang
// emoji không tính là rời đi.
onClickOutside(goc, dongBangChon);
useEventListener('keydown', su_kien => {
  if (su_kien.key === 'Escape' && hienBangChon.value) dongBangChon();
});

const nhan = computed(() => ({
  traLoi: t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
  chuyenTiep: t('BONBOZ.TIN_NHAN.CHUYEN_TIEP'),
  camXuc: props.daThaCamXuc
    ? t('BONBOZ.TIN_NHAN.GO_CAM_XUC')
    : t('BONBOZ.TIN_NHAN.THA_CAM_XUC'),
  them: t('BONBOZ.TIN_NHAN.THEM'),
}));

const chonEmoji = emoji => {
  hienBangChon.value = false;
  emit('camXuc', emoji);
};

const bamCamXuc = () => {
  // Đã thả rồi thì bấm thẳng là gỡ, khỏi mở bảng chọn để bấm lại cái đang có.
  if (props.daThaCamXuc) {
    emit('goCamXuc');
    return;
  }
  hienBangChon.value = !hienBangChon.value;
};

const moMenu = async event => {
  hienBangChon.value = false;
  await nextTick();
  emit('moMenu', event);
};
</script>

<template>
  <!-- Xếp dọc: tên trên, viên thuốc dưới. Align về phía bubble chứ không về mép
       ngoài lane, để tên luôn nằm sát tin nó chú thích.
       Luật HIỆN/ẨN không nằm ở đây mà ở cuối `Message.vue`: nó phải bọc trong
       `@media (hover: hover)`, mà biến thể `group-hover:` của Tailwind thì
       không bọc được. -->
  <div
    ref="goc"
    class="bonboz-thanh-cong-cu flex flex-col gap-0.5 opacity-0 pointer-events-none transition-opacity"
    :class="phia === 'left' ? 'items-end' : 'items-start'"
    @mouseleave="dongBangChon"
  >
    <span v-if="chuThich" class="bonboz-ten-lan">{{ chuThich }}</span>
    <!-- Viền thuốc bo tròn: bốn nút rời trông rời rạc và dễ lẫn vào chữ xung
         quanh; gom vào một khối có nền thì mắt nhận ra ngay đây là một cụm
         điều khiển, không phải một phần của tin.
         Viên thuốc không có padding: 30px viên thuốc = 30px nút = 30px icon,
         không còn pixel nào chưa dùng. Cụm cao 43px (tên 11 + gap 2 + viên
         thuốc 30), thấp hơn bubble một dòng (44px) nên không kéo hàng tin. -->
    <div class="flex relative gap-px items-center rounded-full bg-n-alpha-2">
      <button
        v-if="choPhepTraLoi"
        v-tooltip.top="nhan.traLoi"
        type="button"
        :aria-label="nhan.traLoi"
        class="flex justify-center items-center rounded-full size-[30px] text-n-slate-12 hover:bg-n-solid-1"
        @click="emit('traLoi')"
      >
        <Icon icon="i-ph-arrow-bend-up-left-bold" class="size-[30px]" />
      </button>
      <button
        v-tooltip.top="nhan.chuyenTiep"
        type="button"
        :aria-label="nhan.chuyenTiep"
        class="flex justify-center items-center rounded-full size-[30px] text-n-slate-12 hover:bg-n-solid-1"
        @click="emit('chuyenTiep')"
      >
        <Icon icon="i-ph-arrow-bend-up-right-bold" class="size-[30px]" />
      </button>
      <!-- Nút cảm xúc bọc trong lớp `relative` RIÊNG để bảng chọn neo vào chính
           nó. Trước đây bảng chọn neo vào cả viên thuốc nên nó bám mép viên
           thuốc, lệch hẳn sang một bên so với cái nút vừa được bấm. -->
      <div v-if="choPhepCamXuc" class="flex relative">
        <button
          v-tooltip.top="nhan.camXuc"
          type="button"
          :aria-label="nhan.camXuc"
          class="flex justify-center items-center rounded-full size-[30px] text-n-slate-12 hover:bg-n-solid-1"
          :class="{ 'text-n-brand': daThaCamXuc }"
          @click="bamCamXuc"
        >
          <Icon icon="i-ph-smiley-bold" class="size-[30px]" />
        </button>

        <!-- Vẽ TRONG dòng chứ không teleport ra body: teleport thì nó không
             thuộc khung chat, cuộn một cái là nó đứng yên rồi trôi đè lên khung
             soạn tin. Cùng lý do với chú thích của chip cảm xúc.
             `left-1/2 -translate-x-1/2` canh tâm bảng chọn trùng tâm nút. -->
        <div
          v-if="hienBangChon"
          class="flex absolute bottom-full left-1/2 z-30 gap-0.5 items-center px-1.5 py-1 mb-1.5 rounded-full border shadow-lg -translate-x-1/2 border-n-weak bg-n-solid-1"
        >
          <!-- Hai thứ giữ emoji trùng tâm vòng tròn xám:
               `flex` canh tâm — mặc định thẻ `button` xếp chữ theo baseline, mà
               ký tự emoji chìm một phần dưới baseline nên hình vẽ tụt thấp hơn
               tâm nút. Bốn nút trong viên thuốc vốn đã canh bằng flex, riêng
               chỗ này bỏ sót.
               `size-[1.6em]` — bề ngang tính theo CỠ CHỮ của chính nút, không
               phải một con số cố định. Đổi `text-lg` thành cỡ khác thì vòng
               tròn tự co giãn theo, không lệch lại. -->
          <button
            v-for="emoji in emojiChoPhep"
            :key="emoji"
            type="button"
            :aria-label="emoji"
            class="flex justify-center items-center text-lg leading-none rounded-full transition-transform size-[1.6em] hover:scale-125 hover:bg-n-alpha-2"
            @click="chonEmoji(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
      <button
        ref="nutMenu"
        v-tooltip.top="nhan.them"
        type="button"
        :aria-label="nhan.them"
        class="flex justify-center items-center rounded-full size-[30px] text-n-slate-12 hover:bg-n-solid-1"
        @click="moMenu"
      >
        <Icon icon="i-ph-dots-three-bold" class="size-[30px]" />
      </button>
    </div>
  </div>
</template>
